import React, { useState } from "react";
import { PROJECTS, NEW_PROJECTS } from "../../mock";
import { ArrowUpRight, Facebook } from "lucide-react";

const FALLBACK_FB_IMG = (title) =>
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 600'>
      <defs>
        <linearGradient id='g' x1='0%' y1='0%' x2='100%' y2='100%'>
          <stop offset='0%' stop-color='#1877F2'/>
          <stop offset='100%' stop-color='#0a5fd1'/>
        </linearGradient>
        <pattern id='dots' width='40' height='40' patternUnits='userSpaceOnUse'>
          <circle cx='20' cy='20' r='1.5' fill='rgba(255,255,255,0.1)'/>
        </pattern>
      </defs>
      <rect width='800' height='600' fill='url(#g)'/>
      <rect width='800' height='600' fill='url(#dots)'/>
      <g transform='translate(400,240)' fill='white'>
        <circle r='90' fill='rgba(255,255,255,0.15)'/>
        <path transform='translate(-50,-75) scale(0.45)' fill='white' d='M204.13 32.36c-94.78 0-171.6 76.82-171.6 171.6 0 85.66 62.71 156.66 144.7 169.5V253.78H143.7v-49.82h33.53v-25.55c0-33.07 19.7-51.34 49.86-51.34 14.45 0 29.55 2.58 29.55 2.58v32.46h-16.65c-16.4 0-21.51 10.18-21.51 20.62v24.73h36.6l-5.86 49.82h-30.74v119.68c81.99-12.84 144.7-83.84 144.7-169.5 0-94.78-76.82-171.6-171.6-171.6z'/>
      </g>
      <text x='400' y='420' text-anchor='middle' font-family='Inter,system-ui,sans-serif' font-size='34' font-weight='700' fill='white'>${title.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</text>
      <text x='400' y='460' text-anchor='middle' font-family='Inter,system-ui,sans-serif' font-size='18' font-weight='500' fill='rgba(255,255,255,0.85)'>Facebook page managed by Andy Watson IT</text>
    </svg>`
  );

export default function WorkShowcase() {
  const [filter, setFilter] = useState("All");
  const categories = ["All", ...new Set(PROJECTS.map((p) => p.category))];
  const filtered =
    filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <section id="work" className="py-24 lg:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="section-eyebrow">Selected work</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
              Real projects, real results.
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                  filter === c
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:border-sky-400 hover:text-sky-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p, i) => <ProjectCard key={p.title} p={p} i={i} />)}
        </div>

        <div className="mt-20" data-testid="new-projects-section">
          <span className="section-eyebrow">Just launched</span>
          <h3 className="mt-4 text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">New projects</h3>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {NEW_PROJECTS.map((p, i) => <ProjectCard key={p.title} p={p} i={i} />)}
          </div>
        </div>
      </div>
    </section>
  );
}

const ProjectCard = ({ p, i }) => {
        const Tag = p.url ? "a" : "article";
        const linkProps = p.url
          ? { href: p.url, target: "_blank", rel: "noopener noreferrer" }
          : {};
        return (
          <Tag
            key={p.title}
            {...linkProps}
            className="project-card card-lift group relative rounded-3xl overflow-hidden border border-slate-200 bg-white block"
          >
            <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
              <img
                src={p.platform === "facebook" ? FALLBACK_FB_IMG(p.title) : p.image}
                alt={`${p.title} — ${p.category} project by Andy Watson IT, Lanarkshire`}
                loading={i > 2 ? "lazy" : "eager"}
                onError={(e) => {
                  if (p.platform === "facebook") {
                    e.currentTarget.src = FALLBACK_FB_IMG(p.title);
                  }
                }}
                className="project-img w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/0 to-transparent" />
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <div className="px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[11px] font-semibold text-sky-700">
                  {p.category}
                </div>
                {p.platform === "facebook" && (
                  <div className="h-7 w-7 rounded-full bg-[#1877F2] text-white flex items-center justify-center shadow-sm" aria-label="Facebook project">
                    <Facebook className="h-3.5 w-3.5" />
                  </div>
                )}
              </div>
              <div className="project-overlay absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100">
                <div className="text-white text-xs font-medium">
                  {p.result}
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-start justify-between gap-4">
              <h3 className="text-lg font-semibold text-slate-900" data-testid={`project-title-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {p.title}
                </h3>
                <ArrowUpRight className="h-5 w-5 text-slate-400 group-hover:text-sky-600 transition-colors" />
              </div>
              <div className="mt-2 text-sm text-slate-500">{p.result}</div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {p.tags.map((t) => (
                  <span
                    key={t}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </Tag>
        );
};
